import React, { useEffect } from "react";
import { Card, Box, useTheme, useMediaQuery, Typography } from "@mui/material";
import FeaturedFormCard from "./FeaturedFormCard";
import { AtticusForm } from "../../models/AtticusForm";
import { estateFormsApi } from "apis";
import { GeographyJson } from "models/Geography";

const FeaturedFormCarousel = (props: {
  estateId?: number;
  dataKey?: string;
}): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [featuredForms, setFeaturedForms] = React.useState<
    | {
        percentMatch: number;
        form: AtticusForm;
        formGeography: GeographyJson;
      }[]
    | null
  >(null);
  const [loadingFeaturedForms, setLoadingFeaturedForms] =
    React.useState<boolean>(true);
  const [cards, setCards] = React.useState<JSX.Element[] | null>([]);

  useEffect(() => {
    const myCards = [];
    const numForms = featuredForms ? featuredForms.length : 8;
    for (let i = 0; i < numForms; i++) {
      // Reduce the match by 1-2% if it's 100%
      let percentMatch = 50;
      if (featuredForms && featuredForms[i]) {
        if (featuredForms[i].percentMatch) {
          percentMatch = featuredForms[i].percentMatch;
        }
        if (percentMatch == 1) {
          let reduction = 0.02;
          // This modular math lets us be consistently random across forms
          if (featuredForms[i].form?.id && featuredForms[i].form?.id % 2 == 1) {
            reduction = 0.01;
          }
          percentMatch -= reduction;
        }
      }

      myCards.push(
        <Card sx={{ height: "270px", width: "200px" }}>
          <FeaturedFormCard
            percentMatch={percentMatch}
            form={
              featuredForms && featuredForms[i] && featuredForms[i].form
                ? featuredForms[i].form
                : null
            }
            formGeography={
              featuredForms &&
              featuredForms[i] &&
              featuredForms[i].formGeography
                ? featuredForms[i].formGeography
                : null
            }
          />
        </Card>
      );
    }
    setCards(myCards);
  }, [featuredForms]);

  useEffect(() => {
    if (!props.estateId) {
      console.log("no estate selected");
      return;
    }

    estateFormsApi
      .getRecommendedFormsForEstate(props.estateId)
      .then((forms) => {
        setLoadingFeaturedForms(false);
        if (!forms || forms.length === 0) {
          console.log("no forms fetched for estate " + props.estateId);
          return;
        }
        setFeaturedForms(forms);
        console.log("got forms");
      });
  }, [props.estateId, loadingFeaturedForms, props.dataKey]);

  if (!loadingFeaturedForms && (!featuredForms || featuredForms.length === 0)) {
    return <></>;
  }

  return (
    <Box
      sx={{
        borderRadius: "12px",
        padding: "20px",
        paddingBottom: "10px",
        marginTop: "2rem",
        backgroundColor: "white",
        boxShadow:
          "0px 1px 10px 0px rgba(116, 135, 164, 0.10), 0px 4px 5px 0px rgba(116, 135, 164, 0.11), 0px 2px 4px -1px rgba(116, 135, 164, 0.16)",
      }}
    >
      <Typography variant="h5" sx={{ color: "#2d2a5c", fontWeight: "bold" }}>
        Some other estate forms users like you have found helpful
      </Typography>
      <Typography variant="body1" style={{ marginTop: "1rem" }}>
        Here are some forms that others with a similar scenario have needed, in
        case you end up needing them as well.
      </Typography>
      <Box
        sx={{
          marginTop: "1rem",
          display: "flex",
          gap: isMobile ? 1 : 2,
          py: 1,
          overflow: "auto",
          width: "100%",
          scrollSnapType: "x mandatory",
          "& > *": {
            scrollSnapAlign: "center",
          },
          "&::-webkit-scrollbar": {
            height: "0.2em",
          },
          "&::-webkit-scrollbar-track": {
            marginTop: "50px",
            WebkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ebebf8",
            outline: "1px solid #ebebf8",
            borderRadius: "2px",
          },
        }}
      >
        {cards?.map((card, index) => (
          <Box key={index} sx={{ marginBottom: "5px" }}>
            {card}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FeaturedFormCarousel;
